// Usage & credits: a server balance/history statement with a routed
// Add credits dialog and the retained local placeholder receipt.
//
// Available credits is the REAL balance from GET /sparks and never anything
// else. A sandbox purchase does not move it. Live purchases are fulfilled by
// the backend only after provider verification and the real-money gate; the
// browser return alone never establishes that credits were granted.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { Download, CircleCheck, Gem } from 'lucide-react'

import { useAuth } from '../../../../contexts/AuthContext'
import { useSparks, useBillingCatalog, useCheckoutCountries } from '../../../../api/hooks'
import { api, ApiRequestError } from '../../../../api/client'
import { isApiRecord } from '../../../../api/responseDecoders'
import { useBooleanFlagValue } from '../../../../hooks/useBooleanFlagValue'
import { cn } from '../../../../lib/utils'
import { createNadDebug } from '../../../../utils/debugLog'
import CreditHistory from '../../../../features/billing/CreditHistoryPanel'
import AddCreditsDialog from '../../../../features/billing/AddCreditsDialog'
import Select from '../../../ui/Select'
import SandboxNotice from '../../../../features/billing/SandboxNotice'
import {
  formatCredits,
  formatMoney,
  type CreditPack,
  type SandboxOrder,
  type CheckoutDestination,
} from '../../../../features/billing/billingTypes'
import {
  listOrders,
  saveOrder,
  subscribeToOrders,
} from '../../../../features/billing/sandboxOrderStore'
import { browserTimeZone, countryForTimeZone } from '../../../../features/billing/countryQuote'
import { formatPackPrice } from '../../../../features/billing/packPrice'
import { visibleDiscountPercent } from '../../../../features/billing/packSaving'
import { SAMPLE_RECEIPT_URL, sandboxReceiptUrl, sandboxWalletLabel } from '../../../../features/billing/receipt'
import { useSettingsModal } from '../SettingsModalProvider'
import {
  trackCheckoutInitiateClick,
  trackTokenCatalogOptionClick,
  trackTokenCatalogView,
  trackUsageAndTokensView,
} from '../../../../analytics/events'
import { useActiveTeam } from '../../../../contexts/ActiveTeamContext'
import { useTeamsEnabled } from '../../../../features/teams/useTeamsEnabled'
import { canViewPostpaidUsage } from '../../../../features/billing/teamBillingMode'
import PostpaidUsageSummary from '../../../../features/billing/PostpaidUsageSummary'

const log = createNadDebug('dw:billing-sandbox')

/**
 * The provider's own words for why checkout could not start.
 *
 * The API forwards `provider_detail` / `provider_code` from DCP on a refusal.
 * Collapsing those into one generic string is exactly what principle 7 exists
 * to prevent: "the app or catalog item is not allowed" tells an operator which
 * knob to turn, and "please try again" does not.
 */
function providerReason(error: ApiRequestError): string | null {
  if (!isApiRecord(error.errorData)) return null
  // FastAPI nests our structured payload under `detail`.
  const detail = error.errorData.detail
  if (!isApiRecord(detail)) return null
  // `??` alone keeps an empty string, which then fails the emptiness check and
  // discards the usable `message` sitting next to it.
  const specific = detail.provider_detail
  if (typeof specific === 'string' && specific !== '') return specific
  const fallback = detail.message
  return typeof fallback === 'string' && fallback !== '' ? fallback : null
}

function countryErrorCode(error: Error | null): string | null {
  if (!(error instanceof ApiRequestError) || error.status !== 422 || !isApiRecord(error.errorData)) return null
  const detail = error.errorData.detail
  return isApiRecord(detail) && typeof detail.code === 'string' ? detail.code : null
}

type View = 'credits' | 'add'

export default function UsageAndCreditsSection() {
  const { t } = useTranslation('settings')
  const { teams, activeTeam, isLoading: teamsLoading } = useActiveTeam()
  const { billingTeamId } = useSettingsModal()
  const navigate = useNavigate()
  const location = useLocation()
  const billingTeam = billingTeamId
    ? teams.find((team) => team.id === billingTeamId) ?? null
    : activeTeam
  const showPostpaid = canViewPostpaidUsage(billingTeam)

  useEffect(() => {
    if (showPostpaid && location.pathname === '/settings/billing/add-credits') {
      navigate({ pathname: '/settings/billing', search: location.search }, { replace: true })
    }
  }, [location.pathname, location.search, navigate, showPostpaid])

  if (
    teamsLoading ||
    (billingTeamId !== null && billingTeamId !== undefined && billingTeam === null)
  ) {
    return <p role="status" className="text-base text-muted-foreground">{t('settingsModal.postpaidUsage.loading')}</p>
  }

  if (showPostpaid && billingTeam) {
    return <PostpaidUsageSummary team={billingTeam} />
  }
  return <PrepaidUsageAndCreditsSection />
}

function PrepaidUsageAndCreditsSection() {
  const { t, i18n } = useTranslation('settings')
  const { teams, personalTeam } = useActiveTeam()
  const teamsEnabled = useTeamsEnabled()
  const eligibleTeams = useMemo(
    () => teamsEnabled ? teams.filter((team) => team.id !== personalTeam?.id && (team.myRole === 'owner' || team.myRole === 'admin')) : [],
    [teams, teamsEnabled, personalTeam?.id],
  )
  const { billingTeamId } = useSettingsModal()
  const [requestedTeamId, setRequestedTeamId] = useState<string | null>(billingTeamId ?? null)
  const selectedTeam = eligibleTeams.find((team) => team.id === requestedTeamId) ?? null
  const destination = useMemo<CheckoutDestination>(() => selectedTeam
    ? { kind: 'team', teamId: selectedTeam.id }
    : { kind: 'personal', teamId: null }, [selectedTeam])
  const activeTeamCtx = { owner_type: destination.kind, team_id: destination.teamId ?? undefined }
  // Every formatter below takes the ACTIVE UI locale. Defaulting to en-US
  // would render US grouping, currency placement and date order inside the
  // Korean and Vietnamese bundles.
  const locale = i18n.language
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const { payment, clearPayment, close, entryPoint } = useSettingsModal()
  const userId = user?.id ?? 'anonymous'

  const view: View = location.pathname === '/settings/billing/add-credits' ? 'add' : 'credits'
  const appliedTeamRequest = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (billingTeamId === undefined || appliedTeamRequest.current === billingTeamId) return
    appliedTeamRequest.current = billingTeamId
    setRequestedTeamId(billingTeamId)
    navigate({ pathname: '/settings/billing/add-credits', search: location.search })
  }, [billingTeamId, navigate, location.search])
  const [selectedPackId, setSelectedPackId] = useState<string | null>(null)
  const [orders, setOrders] = useState<SandboxOrder[]>(() => listOrders(userId))
  const [storageError, setStorageError] = useState(false)
  // Live mode creates a real DCP order; the placeholder path does not. This is
  // the mock/live axis only — the @meta.com audience gate is separate and still
  // decides whether this section exists at all.
  const liveCheckout = useBooleanFlagValue('billing_checkout_live_enabled', false)
  const [starting, setStarting] = useState(false)
  const [startError, setStartError] = useState<string | null>(null)

  const [timeZone] = useState(browserTimeZone)
  const countries = useCheckoutCountries(view === 'add')
  const refetchCountries = countries.refetch
  const [requestedCountry, setRequestedCountry] = useState<string | undefined>()
  const [reloadKey, setReloadKey] = useState(0)
  const zoneCountry = countries.data ? countryForTimeZone(timeZone, countries.data) : undefined
  const selectedCountry = requestedCountry ?? zoneCountry
  const readTimeZone = selectedCountry === undefined ? undefined : countries.data?.countryTimeZones[selectedCountry]
  const askingCountry = countries.data !== undefined && selectedCountry === undefined
  const balance = useSparks()
  const catalog = useBillingCatalog(
    view === 'add' && readTimeZone !== undefined, liveCheckout ? 'live' : 'placeholder',
    { timeZone: readTimeZone }, reloadKey,
  )
  const quoteMismatch = liveCheckout && catalog.data !== undefined && catalog.data.countryCode !== selectedCountry
  const catalogFailed = catalog.isError || quoteMismatch
  const countryNames = useMemo(() => new Intl.DisplayNames([locale], { type: 'region' }), [locale])
  const resetCountry = useCallback(() => {
    setRequestedCountry(undefined)
    setSelectedPackId(null)
    setReloadKey((key) => key + 1)
    void refetchCountries()
    setStartError(t('settingsModal.billing.countrySelectionReset'))
  }, [refetchCountries, t])
  useEffect(() => {
    if (requestedCountry !== undefined && countryErrorCode(catalog.error) === 'country_selection_unavailable') resetCountry()
  }, [catalog.error, requestedCountry, resetCountry])

  // settings.usage_and_tokens.view (#3503) — top of the purchase funnel.
  //
  // Fires once per mount, not on every render: the section unmounts when the
  // settings dialog closes, so a mount IS a view. `entry_point` is the deep
  // link when one was used and the user menu otherwise — the two remaining
  // spec values (low_balance_prompt, dashboard_banner) have no surface yet, and
  // are deliberately not guessed at.
  const balanceCredits = balance.data?.balance
  const viewLoggedRef = useRef(false)
  useEffect(() => {
    if (viewLoggedRef.current) return
    viewLoggedRef.current = true
    trackUsageAndTokensView({
      entry_point: entryPoint,
      ...(typeof balanceCredits === 'number' ? { balance_credits: balanceCredits } : {}),
    })
  }, [balanceCredits, entryPoint])

  // Cross-tab: another tab placing an order must show up here.
  useEffect(() => {
    const refresh = () => setOrders(listOrders(userId))
    refresh()
    return subscribeToOrders(userId, refresh)
  }, [userId])

  // Memoised: `catalog.data?.packs ?? []` allocates a fresh array every render, which
  // would make the selection effect below re-run on each one.
  const packs = useMemo(() => catalog.data?.packs ?? [], [catalog.data])
  useEffect(() => {
    if (packs.length === 0) return
    // Re-pick when the current selection is not in this catalog, not only when
    // nothing is selected. The mode flag can flip while the modal is open, and
    // the two catalogs carry different item ids — a stale id leaves `selected`
    // null and Pay now disabled with no visible reason.
    if (selectedPackId !== null && packs.some((p) => p.id === selectedPackId)) return
    setSelectedPackId(packs[0].id)
  }, [packs, selectedPackId])

  // settings.token_catalog.view (#3503) — logged when a catalog actually
  // arrives, once per distinct catalog. Double-logged against the API's
  // token_catalog.fetch.success by design, so client-side loss between the two
  // is measurable; that API half does not exist yet.
  const catalogLoggedRef = useRef<string | null>(null)
  useEffect(() => {
    if (packs.length === 0) return
    const source = liveCheckout ? 'live' : 'placeholder'
    const key = `${source}:${packs.length}`
    if (catalogLoggedRef.current === key) return
    catalogLoggedRef.current = key
    trackTokenCatalogView({
      source,
      item_count: packs.length,
      currency: packs[0].price.currency,
    })
  }, [liveCheckout, packs])

  const successOrder = useMemo(() => {
    if (!payment) return null
    return orders.find((o) => o.orderId === payment.orderId && o.status === 'paid') ?? null
  }, [payment, orders])

  // The LIVE path asks the server to create a real DCP order. The placeholder
  // path below keeps working unchanged for local dev and for any environment
  // without the APNB credential — it is scaffolding, not a fallback, so a
  // failure in live mode is reported rather than silently downgraded.
  const startLiveCheckout = useCallback(
    async (pack: CreditPack) => {
      setStartError(null)
      setStarting(true)
      try {
        const handoff = await api.createCheckoutHandoff(pack.id, destination, {
          timeZone: readTimeZone, countryCode: catalog.data?.countryCode ?? undefined,
        })
        log({
          event: 'checkout:handoff',
          trace_id: `billing:${handoff.orderId}`,
          pack: pack.id,
          mode: handoff.paymentContainerMode,
        })
        close()
        // The handoff code rides in ROUTER STATE, not the URL and not storage.
        // It is a single-use bearer credential for Meta's checkout host; a
        // query param would put it in history, logs and the Referer chain.
        navigate(`/checkout?order=${encodeURIComponent(handoff.orderId)}`, {
          state: { handoff },
        })
      } catch (error) {
        // Stay put and say so. Navigating on a failed start would drop the user
        // on a checkout page with no order to pay for.
        log({
          event: 'checkout:handoff_failed',
          trace_id: 'billing:start',
          pack: pack.id,
        })
        // Principle 7: the backend forwards the provider's status, code and
        // detail, and a generic "please try again" throws away the only part
        // the user or an operator can act on.
        const code = error instanceof Error ? countryErrorCode(error) : null
        if (code === 'country_selection_unavailable') {
          resetCountry()
        } else if (code === 'unsupported_country') {
          setStartError(t('settingsModal.billing.unsupportedCountry'))
        } else {
          const messages: Record<number, string> = {
            403: t('settingsModal.billing.destinationError403'),
            404: t('settingsModal.billing.destinationError404'),
            409: t('settingsModal.billing.destinationError409'),
            422: t('settingsModal.billing.destinationError422'),
          }
          const detail = error instanceof ApiRequestError && isApiRecord(error.errorData)
            ? error.errorData.detail : null
          const providerError = isApiRecord(detail) && (detail.provider_code !== undefined || detail.provider_detail !== undefined)
          const destinationError = error instanceof ApiRequestError && !providerError ? messages[error.status] : null
          const specific = error instanceof ApiRequestError ? providerReason(error) : null
          setStartError(destinationError ?? specific ?? t('settingsModal.billing.checkoutStartFailed'))
        }
      } finally {
        setStarting(false)
      }
    },
    [close, navigate, t, destination, readTimeZone, catalog.data, resetCountry],
  )

  const startCheckout = useCallback(
    (pack: CreditPack) => {
      // Logged before the live/placeholder branch so both funnels are directly
      // comparable, and so an intent is recorded even if order creation then
      // fails.
      trackCheckoutInitiateClick({
        item_id: pack.id,
        source: liveCheckout ? 'live' : 'placeholder',
        credits: pack.credits,
        amount_minor: pack.price.amountMinor,
        currency: pack.price.currency,
        owner_type: activeTeamCtx.owner_type,
        team_id: activeTeamCtx.team_id,
      })
      if (liveCheckout) {
        void startLiveCheckout(pack)
        return
      }
      // Placeholder path: the order id is minted here and carried on the URL,
      // so /checkout can create-or-load idempotently and a refresh there does
      // not duplicate.
      const orderId =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `ord_${Date.now()}_${Math.random().toString(36).slice(2)}`
      const order: SandboxOrder = {
        orderId,
        packId: pack.id,
        credits: pack.credits,
        price: pack.price,
        createdAt: new Date().toISOString(),
        status: 'pending',
        paidAt: null,
        destination,
        destinationName: selectedTeam?.name,
      }
      const written = saveOrder(userId, order)
      log({
        event: 'checkout:start',
        trace_id: `billing-sandbox:${orderId}`,
        pack: pack.id,
        stored: written.ok,
      })
      if (!written.ok) {
        // /checkout loads the order BY ID from this same store, so navigating
        // after a failed write would drop the user on the unknown-order state
        // with no way to place the pack they just chose. Keep them here and
        // say so.
        setStorageError(true)
        return
      }
      setStorageError(false)
      // Dismiss the dialog BEFORE leaving. The modal is provider state owned
      // above <Routes>, not route-scoped, so navigating does not unmount it —
      // without this it stays mounted and covers the checkout page.
      close()
      navigate(`/checkout?order=${encodeURIComponent(orderId)}`)
    },
    [close, liveCheckout, navigate, startLiveCheckout, userId, destination, selectedTeam, activeTeamCtx.owner_type, activeTeamCtx.team_id],
  )

  // ---- payment success -----------------------------------------------------
  if (payment) {
    return (
      <div className="flex flex-col gap-6" data-testid="billing-success">
        <SandboxNotice />


        <div className="flex flex-col items-center gap-4 pt-6 text-center">
          <CircleCheck className="h-14 w-14 text-success" strokeWidth={1.5} />
          <h4 className="text-2xl font-semibold text-white">
            {t('settingsModal.billing.successTitle')}
          </h4>
          {successOrder !== null ? (
            <p className="text-sm text-white/60">
              {t('settingsModal.billing.successBody', {
                credits: formatCredits(successOrder.credits, locale),
              })}
            </p>
          ) : (
            // payment=success with no resolvable order — a stale/foreign link.
            // Non-fatal: say so plainly rather than inventing an amount.
            <p className="text-sm text-white/60" data-testid="billing-success-unknown-order">
              {t('settingsModal.billing.successUnknownOrder')}
            </p>
          )}
        </div>

        {successOrder !== null && (
          <div className="overflow-hidden rounded-2xl bg-white/[0.06]">
            <div className="flex items-center justify-between gap-3 px-4 py-3" data-testid="billing-success-destination">
              <span className="text-sm text-muted-foreground">{t('settingsModal.billing.destination')}</span>
              <span className="min-w-0 break-words text-sm text-foreground">{sandboxWalletLabel(successOrder, t)}</span>
            </div>
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-sm text-white/50">
                {t('settingsModal.billing.product')}
              </span>
              <span className="text-sm text-white">
                {t('settingsModal.billing.packLabel', {
                  credits: formatCredits(successOrder.credits, locale),
                })}
              </span>
            </div>
            <div className="flex items-center justify-between border-t border-white/[0.06] px-4 py-3">
              <span className="text-sm text-white/50">
                {t('settingsModal.billing.amountPaid')}
              </span>
              <span className="text-sm font-medium text-white">
                {formatMoney(successOrder.price, locale)}
              </span>
            </div>
          </div>
        )}

        <div className="flex flex-col items-center gap-3">
          <a
            href={successOrder ? sandboxReceiptUrl(successOrder, t, locale) : SAMPLE_RECEIPT_URL}
            download={successOrder ? `sandbox-receipt-${successOrder.orderId}.txt` : true}
            data-testid="billing-download-receipt"
            className="inline-flex items-center gap-2 text-sm text-white/70 underline-offset-4 hover:text-white hover:underline"
          >
            <Download className="h-4 w-4" />
            {t('settingsModal.billing.downloadReceipt')}
          </a>
          <button
            type="button"
            data-testid="billing-success-done"
            onClick={clearPayment}
            className="h-10 w-full max-w-[220px] rounded-full bg-white text-sm font-semibold text-black transition-colors hover:bg-white/90"
          >
            {t('settingsModal.billing.done')}
          </button>
        </div>
      </div>
    )
  }

  // ---- nested Add credits (pack renderer owned by discount parity) --------
  const selected = packs.find(pack => pack.id === selectedPackId) ?? null
  const tileColumns = packs.length === 1 ? 'grid-cols-1' : packs.length === 2
    ? 'grid-cols-2' : packs.length === 3 ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-2 sm:grid-cols-4'
  const addDialog = view === 'add' ? (
    <AddCreditsDialog selected={!catalogFailed && !catalog.isFetching ? selected : null}
      personalBalance={balance.data?.balance ?? undefined} teamId={selectedTeam?.id ?? null}
      starting={starting} needsCountry={readTimeZone === undefined}
      canContinue={selected !== null && !catalog.isFetching && !catalogFailed && !countries.isFetching}
      error={startError ?? (storageError ? t('settingsModal.billing.storageUnavailable') : null)}
      onClose={() => navigate({ pathname: '/settings/billing', search: location.search }, { replace: true })}
      onContinue={() => { if (selected && !starting) startCheckout(selected) }}>
      {!liveCheckout && <SandboxNotice />}
        <div className="flex max-w-sm flex-col gap-2">
            <label htmlFor="billing-country" className="text-base font-medium text-foreground">
              {t('settingsModal.billing.country')}
            </label>
            <Select
              id="billing-country"
              data-testid="billing-country-selector"
              value={selectedCountry ?? ''}
              disabled={starting || countries.isLoading}
              aria-describedby={askingCountry ? 'billing-country-prompt' : undefined}
              onChange={(event) => {
                setStartError(null)
                setRequestedCountry(event.target.value)
              }}
            >
              {selectedCountry === undefined && <option value="" disabled>{t('settingsModal.billing.selectCountry')}</option>}
              {(countries.data?.selectableCountries ?? []).map((code) => (
                <option key={code} value={code}>{countryNames.of(code) ?? code}</option>
              ))}
            </Select>
        </div>

        {askingCountry && (
          <p id="billing-country-prompt" className="text-sm text-muted-foreground" data-testid="billing-country-prompt">
            {t('settingsModal.billing.countryPrompt')}
          </p>
        )}

        {(countries.isLoading || catalog.isFetching) && (
          <p className="py-8 text-center text-sm text-white/50" data-testid="billing-catalog-loading">
            {t('settingsModal.billing.loadingCatalog')}
          </p>
        )}
        {(countries.isError || catalogFailed) && (
          <p className="py-8 text-center text-sm text-destructive" data-testid="billing-catalog-error">
            {countryErrorCode(catalog.error) === 'unsupported_country'
              ? t('settingsModal.billing.unsupportedCountry')
              : t('settingsModal.billing.catalogFailed')}
          </p>
        )}

        {teamsEnabled && (
          <div className="flex max-w-sm flex-col gap-2">
            <label htmlFor="billing-destination" className="text-base font-medium text-foreground">{t('settingsModal.billing.destination')}</label>
            <Select
              id="billing-destination"
              data-testid="billing-destination"
              value={selectedTeam?.id ?? ''}
              disabled={starting}
              onChange={(event) => {
                setRequestedTeamId(event.target.value || null)
                setStartError(null)
              }}
            >
              <option value="">{t('settingsModal.billing.personalDestination')}</option>
              {eligibleTeams.map((team) => <option key={team.id} value={team.id}>{team.name}</option>)}
            </Select>
          </div>
        )}

      {readTimeZone !== undefined && <p id="billing-choose-amount" className="text-base font-medium text-foreground">{t('settingsModal.billing.chooseCreditAmount')}</p>}
      <fieldset disabled={starting} className="contents">
        {readTimeZone !== undefined && !catalogFailed && packs.length > 0 && (
          <div className={cn('grid gap-2', tileColumns)} role="radiogroup" aria-labelledby="billing-choose-amount"
            onKeyDown={(event) => {
              const radios = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="radio"]'))
              const index = event.target instanceof HTMLButtonElement ? radios.indexOf(event.target) : -1
              if (index < 0) return
              let next: number
              if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % radios.length
              else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + radios.length) % radios.length
              else if (event.key === 'Home') next = 0
              else if (event.key === 'End') next = radios.length - 1
              else return
              event.preventDefault()
              radios[next].focus()
              radios[next].click()
            }}>

            {packs.map((pack) => {
              const isSelected = pack.id === selectedPackId
              const discountPercent = visibleDiscountPercent(pack)
              const reference = pack.referenceAmountMinor
              const previousPrice = reference !== undefined && reference > pack.price.amountMinor
                ? formatPackPrice({ amountMinor: reference, currency: pack.price.currency }, locale)
                : null
              return (
                <button
                  key={pack.id}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  tabIndex={isSelected ? 0 : -1}
                  data-testid={`billing-pack-${pack.id}`}
                  onClick={() => {
                    trackTokenCatalogOptionClick({
                      item_id: pack.id,
                      source: liveCheckout ? 'live' : 'placeholder',
                      credits: pack.credits,
                      amount_minor: pack.price.amountMinor,
                      currency: pack.price.currency,
                      discount_percent: discountPercent,
                      recommended: pack.recommended,
                      owner_type: activeTeamCtx.owner_type,
                      team_id: activeTeamCtx.team_id,
                    })
                    setSelectedPackId(pack.id)
                  }}
                  className={cn(
                    'flex flex-col items-center justify-center gap-1 rounded-lg border bg-surface-1 p-4 text-center transition-colors hover:bg-surface-3 hover:border-input-border-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                    isSelected ? 'border-foreground ring-1 ring-foreground' : 'border-input-border',
                  )}
                >
                  <span className="flex flex-col items-center gap-1">
                    {previousPrice !== null && (
                      <span className="text-sm text-muted-foreground">
                        <span className="sr-only">{t('settingsModal.billing.standardPrice', { price: previousPrice })}</span>
                        <s aria-hidden="true">{previousPrice}</s>
                      </span>
                    )}
                    <span className="text-base font-semibold text-foreground">{formatPackPrice(pack.price, locale)}</span>
                    <span className="text-sm text-muted-foreground">
                      {t('settingsModal.billing.packLabel', {
                        credits: formatCredits(pack.credits, locale),
                      })}
                    </span>
                      {discountPercent > 0 && (
                        <span data-testid="billing-discount-pill" className="inline-flex rounded-full bg-warning px-2 py-0.5 text-xs font-semibold text-background">
                          {t('settingsModal.billing.percentOff', {
                            percent: discountPercent,
                          })}
                        </span>
                      )}
                  </span>
                </button>
              )
            })}
          </div>
        )}

      </fieldset>
    </AddCreditsDialog>
  ) : null

  // The statement belongs alongside the balance. An unavailable balance
  // is not a zero; buying remains possible in every state.
  return (
    <div className="flex flex-col gap-6" data-testid="billing-credits">
      <p className="text-base text-muted-foreground">{t('settingsModal.billing.pageDescription')}</p>
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-surface-0 p-4">
        <div className="min-w-0 flex-[1_1_9rem]">
          {balance.isLoading ? (
            <p role="status" className="text-base text-muted-foreground" data-testid="billing-balance-loading">{t('settingsModal.billing.loadingBalance')}</p>
          ) : balance.isError || balance.data === undefined || balance.data.balance === null ? (
            <p role="alert" className="text-base text-destructive" data-testid="billing-balance-error">{t('settingsModal.billing.balanceFailed')}</p>
          ) : (
            <div className="flex items-center gap-2 text-xl font-semibold text-foreground" data-testid="billing-balance">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface-2"><Gem className="h-5 w-5" aria-hidden="true" strokeWidth={1.5} /></span>
              <span>{t('settingsModal.billing.remaining', { credits: formatCredits(balance.data.balance, locale) })}</span>
            </div>
          )}
        </div>
        <button type="button" data-testid="billing-add-credits-button" onClick={() => navigate({ pathname: '/settings/billing/add-credits', search: location.search })}
          className="ml-auto h-9 shrink-0 rounded-lg bg-foreground px-4 text-base font-medium text-background transition-colors hover:bg-foreground/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
          {t('settingsModal.billing.addCredits')}
        </button>
      </div>
      {!balance.isLoading && !balance.isError && balance.data !== undefined && <CreditHistory />}
      {addDialog}
    </div>
  )
}
